// ============================================================================
// MILES — Blocs SEO partagés (prerender-safe : pas de framer-motion, pas de
// window au rendu). Le head est géré côté client par useSeo et côté build par
// scripts/prerender.js (même source : seoContent.js).
// ============================================================================
import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Baby, Check, Clock, Luggage, Mountain, Plane, Route as RouteIcon, Users } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { VEHICLES } from "@/lib/data";
import { trackEvent } from "@/lib/analytics";
import OVERRIDES from "@/lib/seoOverrides.json";

// overrides édités dans l'admin SEO (MongoDB → seoOverrides.json au build)
export function seoMerge(type, id, lang, title, description) {
  const ov = OVERRIDES[`${type}:${id || ""}:${lang}`];
  return {
    title: ov?.title?.trim() ? ov.title : title,
    description: ov?.meta?.trim() ? ov.meta : description,
    noindex: Boolean(ov?.noindex),
  };
}
import { BASE_URL, BOOKING_PATH, HREFLANG, LANGS, PLACE_COORDS, S, T, p, routeUrl, routeCopy } from "@/lib/seoContent";
import { GUIDES_COPY, guideUrl, guidesForLanding, guidesHubUrl } from "@/lib/guides";

// ---------------------------------------------------------------- head (client)
// Réutilise d'abord les balises natives d'index.html (évite les doublons), sinon crée avec data-seo.
const NATIVE = {
  description: 'meta[name="description"]',
  robots: 'meta[name="robots"]',
  canonical: 'link[rel="canonical"]',
  "og:title": 'meta[property="og:title"]',
  "og:description": 'meta[property="og:description"]',
  "og:url": 'meta[property="og:url"]',
};

const upsert = (attr, key, tag, props) => {
  let el = (NATIVE[key] && document.head.querySelector(NATIVE[key])) || document.head.querySelector(`${tag}[data-seo="${key}"]`);
  if (!el) {
    el = document.createElement(tag);
    el.setAttribute("data-seo", key);
    document.head.appendChild(el);
  }
  Object.entries(props).forEach(([k, v]) => el.setAttribute(k, v));
};

export function useSeo({ lang, title, description, path, type, id, jsonLd = [], noindex = false }) {
  useEffect(() => {
    const merged = type ? seoMerge(type, id, lang, title, description) : { title, description, noindex };
    document.title = merged.title;
    document.documentElement.lang = lang;
    upsert(null, "description", "meta", { name: "description", content: merged.description });
    upsert(null, "robots", "meta", { name: "robots", content: merged.noindex || noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large" });
    upsert(null, "og:title", "meta", { property: "og:title", content: merged.title });
    upsert(null, "og:description", "meta", { property: "og:description", content: merged.description });
    upsert(null, "og:url", "meta", { property: "og:url", content: `${BASE_URL}${path}` });
    upsert(null, "canonical", "link", { rel: "canonical", href: `${BASE_URL}${path}` });
    // dimensions CRO : identifier quelles landing pages convertissent réellement
    trackEvent("page_view", { path, landing_page_type: type || null, landing_page_id: id || null });
    return () => {};
  }, [lang, title, description, path, type, id, noindex]);
}

export function useHreflang(paths) {
  // paths: { fr: "/fr/...", en: "/en/...", de: "/de/..." }
  useEffect(() => {
    if (!paths) return;
    Object.entries(paths).forEach(([l, href]) =>
      upsert(null, `alt-${l}`, "link", { rel: "alternate", hreflang: HREFLANG[l], href: `${BASE_URL}${href}` }));
    // x-default = français (cohérent avec la redirection / → /fr)
    upsert(null, "alt-x-default", "link", { rel: "alternate", hreflang: "x-default", href: `${BASE_URL}${paths.fr}` });
  }, [paths]);
}

// Pages non destinées à Google (booking, tokens, apps authentifiées) —
// le noindex doit être visible par Google : ces pages ne sont PAS bloquées via robots.txt.
export function useRobots(content) {
  useEffect(() => {
    upsert(null, "robots", "meta", { name: "robots", content });
  }, [content]);
}

export function JsonLd({ data, id }) {
  return <script type="application/ld+json" data-jsonld={id} dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }} />;
}

// ---------------------------------------------------------------- layout
export function SeoPage({ lang, breadcrumbs = [], children, testId }) {
  return (
    <div className="min-h-screen bg-white" data-testid={testId}>
      <Navbar />
      <main className="mx-auto max-w-6xl px-5 pb-20 pt-28 sm:px-8">
        {breadcrumbs.length > 1 && <Breadcrumbs items={breadcrumbs} />}
        {children}
      </main>
      <Footer />
    </div>
  );
}

export function Breadcrumbs({ items }) {
  // items: [{ label, to? }] — le dernier est la page courante
  return (
    <nav aria-label="breadcrumb" className="mb-8" data-testid="breadcrumbs">
      <ol className="flex flex-wrap items-center gap-1.5 text-xs text-slate">
        {items.map((it, i) => (
          <li key={i} className="flex items-center gap-1.5">
            {i > 0 && <span className="text-slate/40">/</span>}
            {it.to && i < items.length - 1
              ? <Link to={it.to} className="transition-colors hover:text-miles">{it.label}</Link>
              : <span className={i === items.length - 1 ? "font-medium text-ink" : ""}>{it.label}</span>}
          </li>
        ))}
      </ol>
    </nav>
  );
}

// ---------------------------------------------------------------- hero route (GVA ↓ 160 km ↓ Verbier)
export function RouteHero({ lang, route, copy }) {
  const t = T[lang];
  return (
    <div className="overflow-hidden rounded-3xl border border-ink/10 bg-white shadow-[0_30px_80px_-50px_rgba(11,31,77,0.35)]" data-testid="route-hero">
      <div className="grid gap-0 md:grid-cols-[1.2fr_1fr]">
        <div className="p-7 sm:p-10">
          <div className="flex items-center gap-3">
            <div className="min-w-0">
              <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-slate">{t.from}</p>
              <p className="mt-1 truncate text-lg font-bold text-ink sm:text-xl" data-testid="route-origin">{copy.originName}</p>
            </div>
            <div className="flex shrink-0 flex-col items-center px-1 text-miles">
              <ArrowRight className="h-5 w-5" strokeWidth={1.5} />
            </div>
            <div className="min-w-0">
              <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-slate">{t.to}</p>
              <p className="mt-1 truncate text-lg font-bold text-ink sm:text-xl" data-testid="route-destination">{copy.destName}</p>
            </div>
          </div>
          <div className="mt-6 flex flex-wrap gap-x-8 gap-y-3 border-t border-ink/[0.06] pt-6">
            {copy.km && <Fact label={t.distance} value={`≈ ${copy.km} km`} testId="route-distance" />}
            {copy.dur && <Fact label={t.duration} value={`≈ ${copy.dur}`} testId="route-duration" />}
            {copy.price && <Fact label={`MILES Business`} value={`${t.price_from} CHF ${copy.price}.–`} testId="route-price" accent />}
          </div>
          <p className="mt-6 max-w-xl text-sm leading-relaxed text-slate">{copy.intro}</p>
        </div>
        <div className="border-t border-ink/[0.06] bg-miles-ice/40 p-7 sm:p-10 md:border-l md:border-t-0">
          <RouteBookingCta lang={lang} route={route} copy={copy} />
        </div>
      </div>
    </div>
  );
}

const Fact = ({ label, value, testId, accent = false }) => (
  <div>
    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate">{label}</p>
    <p className={`mt-0.5 font-mono text-base font-semibold ${accent ? "text-miles" : "text-ink"}`} data-testid={testId}>{value}</p>
  </div>
);

// ---------------------------------------------------------------- CTA réservation pré-rempli
export function RouteBookingCta({ lang, route, copy, compact = false }) {
  const t = T[lang];
  const o = PLACE_COORDS[route.origin];
  const d = PLACE_COORDS[route.destination];
  const params = new URLSearchParams({ type: "one_way", from: copy.originName, to: copy.destName });
  if (o) { params.set("from_lat", o[0]); params.set("from_lon", o[1]); }
  if (d) { params.set("to_lat", d[0]); params.set("to_lon", d[1]); }
  const href = `${BOOKING_PATH}?${params.toString()}`;
  return (
    <div data-testid="route-booking-cta">
      <div className="space-y-2.5">
        <div className="rounded-xl border border-ink/10 bg-white px-4 py-3">
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate">{t.from}</p>
          <p className="truncate text-sm font-semibold text-ink">{copy.originName}</p>
        </div>
        <div className="rounded-xl border border-ink/10 bg-white px-4 py-3">
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate">{t.to}</p>
          <p className="truncate text-sm font-semibold text-ink">{copy.destName}</p>
        </div>
        <Link to={href} data-testid="route-book-button"
          className="flex h-12 items-center justify-center gap-2 rounded-xl bg-miles text-sm font-semibold text-white transition-opacity hover:opacity-90">
          {t.book_route} <ArrowRight className="h-4 w-4" strokeWidth={2} />
        </Link>
      </div>
      {!compact && (
        <ul className="mt-4 space-y-1.5">
          {[t.flight_tracking, t.fixed_price, t.meet_greet, t.free_waiting].map((f) => (
            <li key={f} className="flex items-center gap-2 text-xs text-slate">
              <Check className="h-3.5 w-3.5 shrink-0 text-emerald-500" strokeWidth={2} /> {f}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- véhicules
export function VehicleCards({ lang, route }) {
  const t = T[lang];
  const prices = route?.metrics?.prices || null;
  return (
    <div className="grid gap-3 sm:grid-cols-3" data-testid="vehicle-cards">
      {Object.values(VEHICLES).map((v) => (
        <div key={v.id} className="overflow-hidden rounded-2xl border border-ink/10 bg-white" data-testid={`vehicle-card-${v.id}`}>
          <div className="flex h-28 items-center justify-center bg-gradient-to-b from-white to-miles-ice/60">
            <img src={v.image} alt={v.name} className="h-full w-full object-contain p-4" loading="lazy" />
          </div>
          <div className="px-4 pb-4 pt-3">
            <p className="text-sm font-semibold text-ink">{v.name}</p>
            <p className="mt-1 flex items-center gap-3 text-[11px] text-slate">
              <span className="inline-flex items-center gap-1"><Users className="h-3 w-3" /> {v.pax} {t.passengers}</span>
              <span className="inline-flex items-center gap-1"><Luggage className="h-3 w-3" /> {v.bags} {t.bags}</span>
            </p>
            {prices?.[v.id] != null && (
              <p className="mt-2 font-mono text-[13px] font-semibold text-miles">CHF {Math.round(prices[v.id])}.–</p>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------- preuves
export function TrustChips({ lang, ski = false }) {
  const t = T[lang];
  const items = [
    { icon: Plane, label: t.flight_tracking },
    { icon: Check, label: t.fixed_price },
    { icon: Clock, label: t.free_waiting },
    { icon: Users, label: t.human_support },
    { icon: Baby, label: t.child_seat },
    ...(ski ? [{ icon: Mountain, label: t.skis_ok }] : []),
  ];
  return (
    <div className="flex flex-wrap gap-2" data-testid="trust-chips">
      {items.map(({ icon: Icon, label }) => (
        <span key={label} className="inline-flex items-center gap-1.5 rounded-full border border-ink/10 bg-white px-3 py-1.5 text-xs font-medium text-ink">
          <Icon className="h-3.5 w-3.5 text-miles" strokeWidth={1.5} /> {label}
        </span>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------- FAQ
export function FaqBlock({ lang, faq }) {
  const t = T[lang];
  return (
    <section data-testid="faq-block">
      <h2 className="text-lg font-bold text-ink">{t.faq_title}</h2>
      <div className="mt-4 divide-y divide-ink/[0.06] rounded-2xl border border-ink/10 bg-white">
        {faq.map(([q, a]) => (
          <details key={q} className="group px-5 py-4">
            <summary className="cursor-pointer list-none text-sm font-semibold text-ink transition-colors group-open:text-miles">{q}</summary>
            <p className="mt-2 text-sm leading-relaxed text-slate">{a}</p>
          </details>
        ))}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------- maillage interne
export function RouteLinks({ title, routes, lang, testId }) {
  if (!routes.length) return null;
  const t = T[lang];
  return (
    <section data-testid={testId}>
      <h2 className="text-lg font-bold text-ink">{title}</h2>
      <div className="mt-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {routes.map((r) => {
          const c = routeCopy(r, lang);
          return (
            <Link key={r.id} to={routeUrl(r, lang)} data-testid={`route-link-${r.id}`}
              className="group flex items-center justify-between gap-2 rounded-xl border border-ink/10 bg-white px-4 py-3 transition-colors hover:border-miles/40">
              <span className="min-w-0">
                <span className="block truncate text-sm font-semibold text-ink">{c.originName} → {c.destName}</span>
                <span className="mt-0.5 block font-mono text-[11px] text-slate">
                  {c.km ? `≈ ${c.km} km · ${c.dur}` : ""}{c.price ? ` · ${t.price_from} CHF ${c.price}.–` : ""}
                </span>
              </span>
              <ArrowRight className="h-4 w-4 shrink-0 text-slate transition-transform group-hover:translate-x-0.5 group-hover:text-miles" strokeWidth={1.5} />
            </Link>
          );
        })}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------- landing commerciale → guides utiles
// Rendu uniquement si des guides publiés soutiennent explicitement cette page (champ landing_page du CMS).
export function GuideLinks({ lang, path }) {
  const guides = guidesForLanding(lang, path);
  if (!guides.length) return null;
  const c = GUIDES_COPY[lang];
  const title = { fr: "Guides utiles avant de réserver", en: "Useful guides before you book", de: "Nützliche Ratgeber vor der Buchung" }[lang];
  return (
    <section data-testid="guide-links">
      <div className="flex items-end justify-between gap-4">
        <h2 className="text-lg font-bold text-ink">{title}</h2>
        <Link to={guidesHubUrl(lang)} className="text-xs font-semibold text-miles hover:underline">{c.all_in}</Link>
      </div>
      <div className="mt-4 grid gap-2 sm:grid-cols-2">
        {guides.map((g) => (
          <Link key={g.id} to={guideUrl(g)} data-testid={`guide-link-${g.slug}`}
            className="group flex items-center justify-between gap-3 rounded-xl border border-ink/10 bg-white px-4 py-3 transition-colors hover:border-miles/40">
            <span className="min-w-0">
              <span className="block truncate text-sm font-semibold text-ink">{g.title}</span>
              {g.summary && <span className="mt-0.5 block truncate text-[12px] text-slate">{g.summary}</span>}
            </span>
            <ArrowRight className="h-4 w-4 shrink-0 text-slate transition-transform group-hover:translate-x-0.5 group-hover:text-miles" strokeWidth={1.5} />
          </Link>
        ))}
      </div>
    </section>
  );
}

export function SectionTitle({ kicker, title, intro }) {
  return (
    <div>
      {kicker && <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-miles">{kicker}</p>}
      <h2 className="mt-2 text-xl font-bold text-ink sm:text-2xl">{title}</h2>
      {intro && <p className="mt-3 max-w-2xl text-sm leading-relaxed text-slate">{intro}</p>}
    </div>
  );
}
